import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import {
  Heading,
  InputForm,
  Paper,
  Spacer,
  Text,
  Toggle
} from "src/components";
import { SideMenu } from "src/components/sideMenu";
import { theme } from "src/config";
import { AuthStore, RoutingStore, UserStore, SpaceStore } from "src/stores";
import { SpaceSettings } from "src/types";
import styled from "styled-components";

const SettingsTypeInfo = styled(Text)`
  margin-top: 0.25rem;
`;

export interface INotificationsSettingsProps {
  authStore?:    AuthStore;
  routingStore?: RoutingStore;
  userStore?:    UserStore;
  spaceStore?:   SpaceStore;
}

export const NotificationSettings = inject("routingStore", "authStore", "spaceStore", "userStore")(observer((props: INotificationsSettingsProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const user = props.authStore.getCurUser;

  const sections: {
    title:       string;
    explanation: string;
    props:       (keyof SpaceSettings)[];
    label:       string[];
  }[] = [
    {
      title:       t("participation"),
      explanation: t("settings_participation_info"),
      props:       ["participation"],
      label:       [t("email"), t("online")]
    },{
      title:       t("subscription_of_meeting_rooms"),
      explanation: t("settings_subscription_of_meeting_rooms_info"),
      props:       ["watching"],
      label:       [t("email"), t("online")]
    },{
      title:       t("settings"),
      explanation: t("settings_notifications_general_notifications_info"),
      props:       ["user_changes", "during_meeting"],
      label:       [
        t("include_my_own_changes"),
        t("notify_me_during_active_meetings_im_participating")
      ]
    },{
      title:       t("other_notifications"),
      explanation: t("settings_notifications_other_notifications_info"),
      props:       ["mentions"],
      label:       [t("mentions")]
    },{
      title:       t("subscriptions"),
      explanation: t("settings_notifications_subscriptions_info"),
      props:       ["daily_summary", "reminder"],
      label:       [t("daily_summary"), t("reminders")]
    }
  ];

  if (!user) {
    return <></>;
  }

  return (
    <Paper
      display        = "grid"
      justifyContent = "center"
      colspan        = {10}
      flexDirection  = "row"
      style          = {theme.paper.regular}
      $center
      $noStyling
    >
      <Box display="flex" cols={1}>
        <SideMenu />
      </Box>

      <Box marginLeft="1rem" display="flex" cols={9}>
        <Paper $noShadow width="100%" onChange={user.saveSpaceSettings}>
          <Box gridRowGap={theme.layout.gutter} alignItems="left">
            <Box>
              <Heading type="h3">{t("email_settings")}</Heading>
            </Box>
            <InputForm>
              <Spacer size="xsmall" />
              {sections.map((s) => (
                <InputForm key={s.title}>
                  <Text weight="bold">{s.title}</Text>
                  <Spacer size="xxsmall" />
                  {s.props.map((p, idx) => (
                    <div key={idx}>
                      <Toggle
                        key     = {s.label[idx]}
                        label   = {s.label[idx]}
                        checked = {Boolean(
                          user.spaces[props.authStore.getCurWorkspaceID][p]
                        )} // This will change to room ID
                        onCheckedChange={(val: number) => {
                          user.setSpaceSetting(
                            props.authStore.getCurWorkspaceID,
                            p,
                            val,
                            true
                          ); // change to room ID
                        }}
                      />
                    </div>
                  ))}
                  <SettingsTypeInfo
                    weight    = "thin"
                    fontStyle = "italic"
                    size      = "small"
                  >
                    {s.explanation}
                  </SettingsTypeInfo>
                </InputForm>
              ))}
            </InputForm>
          </Box>
        </Paper>
      </Box>
    </Paper>
  );
}));
